import {useLeaflet} from "react-leaflet";
import {renderToString} from 'react-dom/server';
import React, {useContext, useEffect, useState} from "react";
import L from "leaflet";
import "leaflet.markercluster";
import {Button} from "reactstrap";
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';
import FilterContext from "../FilterContext";
import {ReactSession} from "react-client-session";
import axios from "axios";
import packageJson from '../../package.json';

export const JITTER_RADIUS_M = 100;

// Feature flag for location obfuscation (jitter + approximate-location disclaimers).
// TODO: replace with per-agency configuration once the agency config table is in place.
export const LOCATION_OBFUSCATION_ENABLED = false;

// Deterministic pseudo-random based on a numeric seed (mulberry32 step)
const seededRandom = (seed) => {
    let t = (seed ^ 0xdeadbeef) >>> 0;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

// Stable hash of a string to a 32-bit integer
const hashStr = (s) => {
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 0x01000193) | 0;
    }
    return h >>> 0;
};

// Offset lat/lon by up to JITTER_RADIUS_M, consistently keyed on a feature id
const jitterCoords = (lat, lon, id) => {
    if (!LOCATION_OBFUSCATION_ENABLED) {
        return [lat, lon];
    }
    const seed = hashStr(String(id ?? `${lat},${lon}`));
    const r = JITTER_RADIUS_M * Math.sqrt(seededRandom(seed));
    const theta = seededRandom(seed + 1) * 2 * Math.PI;
    const metersPerDegLat = 111320;
    const metersPerDegLon = 111320 * Math.cos(lat * Math.PI / 180);
    return [
        lat + (r * Math.cos(theta)) / metersPerDegLat,
        lon + (r * Math.sin(theta)) / metersPerDegLon,
    ];
};

export const DynamicGeoJson = ({geoJson, flag, onOpen}) => {

    let l = useLeaflet();
    const leafletMap = l.map;
    const [context] = useContext(FilterContext);

    const [markerClusters] = useState(L.markerClusterGroup({
        spiderfyOnMaxZoom: true,
        disableClusteringAtZoom: 17
    }));

    // heat layer takes in list of lat/lon point
    const [heat] = useState(L.heatLayer([]));

    // cycle the heatmap on/off to fix any race conditions in the lib
    useEffect(() => {
        leafletMap.on('baselayerchange', () => {
            // quickfix for heatmap layer forgetting the map
            heat._map = leafletMap;
        });
    }, []);

    useEffect(() => {
        leafletMap.addLayer(markerClusters);
        // toggle between clusters and heatmap
        const overlayMaps = flag === 'results' ?
            {
                "Cluster Incidents": markerClusters,
                "Heat Map": heat
            }
            :
            {
                "Cluster Calls": markerClusters,
                "Heat Map": heat
            }

        const options = {
            position: "bottomright"
        }
        L.control.layers(overlayMaps, undefined, options).addTo(leafletMap);
    }, [markerClusters]);

    useEffect(() => {

        const crimeTipButtonHandler = () => {
            onOpen()
        };

        //TODO: Remove hardcoded media report options
        const mediaReport = {
            "schemaId" : "FL38",
            "agencyCode" : "FL38000",
            "incidentId" : "12345"
        }
        const mediaReportButtonHandler = () => {
            axios.get(packageJson.homepage+'/api/v1/printIncidentReport', {
                params : mediaReport,
                responseType: "blob" //Response type must be BLOB, default is JSON
            })
                .then(response=>{
                    const rawdata = new Blob( [response.data], {type: "blob"});
                        const data = window.URL.createObjectURL(rawdata);
                        var link = document.createElement('a');
                        link.href = data;
                        link.download="IncidentReport.pdf";
                        link.click();
                        setTimeout(function(){
                            window.URL.revokeObjectURL(data);
                        }, 100);
                })
        }

        const loadButtonHandlers = () => {
            const buttonEl = document.getElementById("crime-tip-button-id");
            if (buttonEl) {
                buttonEl.addEventListener("click", crimeTipButtonHandler);
            }
            const buttonEl2 = document.getElementById("media-report-button-id");
            if (buttonEl2) {
                buttonEl2.addEventListener("click", mediaReportButtonHandler);
            }
        };

        const unloadButtonHandlers = () => {
            const buttonEl = document.getElementById("crime-tip-button-id");
            if (buttonEl) {
              buttonEl.removeEventListener("click", crimeTipButtonHandler);
            }
            const buttonEl2 = document.getElementById("media-report-button-id");
            if (buttonEl2) {
              buttonEl2.removeEventListener("click", mediaReportButtonHandler);
            }
        };

        markerClusters.clearLayers();

        const latlngs = [];

        if (geoJson && geoJson.features) {
        for (let feature of geoJson.features) {
            const [lat, lon] = jitterCoords(
                feature.geometry.coordinates[1],
                feature.geometry.coordinates[0],
                feature.properties?.incidentId ?? feature.properties?.dispatchId
            );
//following code from  https://stackoverflow.com/questions/23567203/leaflet-changing-marker-color
            let myCustomIconSource = '';
            if(flag==="results"){
                myCustomIconSource = 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png';
            }
            else{
                myCustomIconSource = 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png';
            }
            //TODO: below block can be removed if custom icon images works as intended

            // const markerHtmlStyles = `
            //   background-color: ${myCustomColour};
            //   width: 3rem;
            //   height: 3rem;
            //   display: block;
            //   left: -1.5rem;
            //   top: -1.5rem;
            //   position: relative;
            //   border-radius: 3rem 3rem 0;
            //   transform: rotate(45deg);
            //   border: 1px solid #FFFFFF`
            // const icon_1 = L.divIcon({
            //   className: "my-custom-pin",
            //   iconAnchor: [0, 24],
            //   labelAnchor: [-6, 0],
            //   popupAnchor: [0, -36],
            //   html: `<span style="${markerHtmlStyles}" />`
            // })
            const customIcon = new L.Icon({
                iconUrl: myCustomIconSource,
                shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
                iconSize: [25, 41],
                iconAnchor: [12, 41],
                popupAnchor: [1, -34],
                shadowSize: [41, 41]
            });
            const marker = L.marker([lat, lon], {icon: customIcon});

            marker.bindPopup(formatFeature(feature, flag));
            marker.addEventListener("popupopen", loadButtonHandlers);
            marker.addEventListener("popupclose", unloadButtonHandlers);
            markerClusters.addLayer(marker);
            latlngs.push([lat, lon]);
        }

        heat.setLatLngs(latlngs);

        if (!context.filters.region && !context.filters.shapeId
                && geoJson && geoJson.features && geoJson.features.length > 0) {
            l.map.fitBounds(markerClusters.getBounds());
        }

        }

    }, [geoJson, markerClusters])
    return <></>
}


const setAreaStyle = (feature) => {
    return {color: feature.properties.color, weight: 3, fillcolor: feature.properties.color, opacity: 0.8};
}

export const DynamicGeoJsonShapes = ({geoJson}) => {
    let l = useLeaflet();
    const leafletMap = l.map;

    const [geoJsonLayer] = useState(L.geoJson(undefined, {
        style: setAreaStyle,
        onEachFeature: (feature, layer) => {
            layer.on({
                mouseover: highlightFeature,
                mouseout: resetHighlight
            });
            layer.bindTooltip(feature.properties.name, {
                permanent: false,
                direction: "center"
            })
        }
    }));

    useEffect(() => {
        leafletMap.addLayer(geoJsonLayer);
    }, [geoJsonLayer])

    useEffect(() => {
        geoJsonLayer.clearLayers();
        if (geoJson) {
            geoJsonLayer.addData(geoJson);
            l.map.fitBounds(geoJsonLayer.getBounds());
        }
    }, [geoJson, geoJsonLayer])

    return <></>
}

//format info on each marker to pure html string for display
const formatFeature = (feature, flag) => {
    return renderToString(<MarkerInfo feature={feature} flag = {flag}/>);
}

const markerKeys = {
    "occurrenceDate": "Occurrence Date",
    "matched ": "Matched",
    "missed": "Missed",
    "agency": "Agency",
    "eventType": "Event Type",
    "date" : "Date",
    "responseTimeHours": "Response Time (Hours)",
    "callPriority": "Call Priority",
    "description": "Description"

}

//info on each and every marker on the map
const MarkerInfo = ({feature, flag}) => {
    const incId = feature.properties.incidentId;
    const hiddenKeys = new Set(['incidentId', 'dispatchId']);
    const content = Object.entries(feature.properties).filter(([key]) => !hiddenKeys.has(key)).map(kv => {
        const [key, value] = kv;
        const name = markerKeys.hasOwnProperty(key) ? markerKeys[key] : key;
        return <Feature key={key} name={name} value={value}/>;
    })


    if(flag==="results"){
    return <div className="markerinfo">
        <h6>Incident Description</h6>
        <table>
            <tr>
            {content}
            </tr>
        </table>
        <DisplayButtons incId={incId}/>
        {LOCATION_OBFUSCATION_ENABLED && <p className="markerinfo-disclaimer"><span style={{color: '#e65c00', fontSize: '15px'}}>&#9888;</span> <strong>THIS LOCATION IS APPROXIMATE</strong></p>}
    </div>
    }
    else{
    return <div className="markerinfo">
            <h6>Call Description</h6>
            <table>
                {content}
            </table>
            {LOCATION_OBFUSCATION_ENABLED && <p className="markerinfo-disclaimer"><span style={{color: '#e65c00', fontSize: '15px'}}>&#9888;</span> <strong>THIS LOCATION IS APPROXIMATE</strong></p>}
        </div>
    }

}

const DisplayButtons = ({incId}) => {
    return <div></div>
    {/*
    COMMENTING OUT SUBMIT TIP AND PRINT INCIDENT REPORT

    const username = ReactSession.get("Username")
    if (username !== undefined){
        return <div className="markerinfo">
            <Button id="crime-tip-button-id" >Submit Tip</Button>
            {" "}
            <Button id="media-report-button-id" >Print Media Report</Button>
            <iid style={{display:"none"}} id="incId">{incId}</iid>
        </div>
    }else{
        return <div>
            <Button id="crime-tip-button-id" >Submit Tip</Button>
            <iid style={{display:"none"}} id="incId">{incId}</iid>
        </div>
    }
    */}
}

const Feature = ({name, value}) => {
    if (Array.isArray(value)){
        const lst_content = value.map(val => {
        return <p>{val}</p>
        })
        return <tr>
                    <th>{name}</th>
                    <td>{lst_content}</td>
                </tr>
    }
    else{
        return <tr>
            <th>{name}</th>
            <td>{value}</td>
        </tr>
    }
}

const highlightFeature = (e) => {
    e.target.setStyle({color: 'yellow'});
    e.target.bringToFront();
}

function resetHighlight(e) {
    e.target.setStyle({color: e.target.feature.properties.color});
}
